export type PlanKey = "starter" | "challenger" | "master";

// feature.key 는 카피와 무관한 안정 식별자. INFO_CONTENTS 조회와 refundLabel 동적 치환 등 분기에 사용.
// feature.text 는 언제든 카피만 갱신 가능하도록 분리.
export interface PlanFeature {
  key: string;
  text: string;
  included: boolean;
  info?: boolean;
}

export interface PlanItem {
  key: PlanKey;
  name: string;
  price: number;
  // 제휴 할인 적용 시, 취소선으로 표기할 원가. 미할인이면 undefined.
  originalPrice?: number;
  tag?: string;
  features: PlanFeature[];
}

export const planKeyToName: Record<PlanKey, string> = {
  starter: "스타터",
  challenger: "챌린저",
  master: "마스터",
};

// pricingModel "e" / "h" / "i" / "j" / "f" 는 단일 "맞춤 플랜" 카드(challenger 매핑)를 노출하며,
// 각 모델별로 challenger 가격을 오버라이드한다.
export const SINGLE_PLAN_PRICES: Record<string, number> = {
  e: 395000,
  h: 595000,
  i: 695000,
  j: 425000,
  f: 495000,
};

export const isSinglePlanPricingModel = (model: string | undefined): boolean =>
  model !== undefined && model in SINGLE_PLAN_PRICES;

export const getSinglePlanPrice = (
  model: string | undefined
): number | undefined =>
  isSinglePlanPricingModel(model)
    ? SINGLE_PLAN_PRICES[model as string]
    : undefined;

// pricingModel "aeg"/"ajk" 등은 다중 플랜(S/C/M) 카드를 모두 노출하되 각 플랜 가격을 오버라이드한다.
// 기본가는 aeg 기준이며 ajk 는 deprecated(레거시 유지).
export const MULTI_PLAN_PRICES: Record<string, Record<PlanKey, number>> = {
  aeg: { starter: 295000, challenger: 395000, master: 465000 },
  ajk: { starter: 295000, challenger: 425000, master: 625000 },
};

export const getMultiPlanPrices = (
  model: string | undefined
): Record<PlanKey, number> | undefined =>
  model !== undefined ? MULTI_PLAN_PRICES[model] : undefined;

export const applyMultiPlanPrices = (
  basePlans: PlanItem[],
  model: string | undefined
): PlanItem[] => {
  const overrides = getMultiPlanPrices(model);
  if (!overrides) return basePlans;
  return basePlans.map((p) => ({ ...p, price: overrides[p.key] ?? p.price }));
};

// 제휴(affiliate) 한정 할인: 챌린저/마스터에만 -30,000 (스타터 불변). 24h 윈도우는 백엔드가 판정해 active 로 전달.
// 원가는 originalPrice 로 보존(취소선 표기), price 는 할인가. 서버 결제검증(resolvePlan)과 동일 규칙.
export const AFFILIATE_DISCOUNT = 30000;
const AFFILIATE_DISCOUNT_PLAN_KEYS: PlanKey[] = ["challenger", "master"];

export const applyAffiliateDiscount = (
  basePlans: PlanItem[],
  active: boolean
): PlanItem[] => {
  if (!active) return basePlans;
  return basePlans.map((p) =>
    AFFILIATE_DISCOUNT_PLAN_KEYS.includes(p.key)
      ? { ...p, originalPrice: p.price, price: p.price - AFFILIATE_DISCOUNT }
      : p
  );
};

// 제휴 프로모션: user.promotion 으로 구분하며 S/C/M 가격을 전용가로 오버라이드.
// pricingModel 기반 오버라이드(MULTI_PLAN_PRICES)보다 우선한다.
export const PROMOTION_PLAN_PRICES: Record<string, Record<PlanKey, number>> = {
  타이탄교육그룹: { starter: 219000, challenger: 299000, master: 349000 },
  코리안매니아: { starter: 255000, challenger: 315000, master: 385000 },
  삶클리닉: { starter: 295000, challenger: 395000, master: 465000 },
};

const PROMOTION_BANNER_LABELS: Record<string, string> = {
  타이탄교육그룹: "타이탄교육그룹 제휴 혜택 적용",
  코리안매니아: "코리안매니아 제휴 혜택 적용",
  삶클리닉: "삶클리닉 제휴 혜택 적용 (+30일)",
};

// 제휴 프로모션 사용자 여부. 광고 추적(meta/capi/firebase 등) 제외 분기에도 사용.
export const isPromotionUser = (promotion: string | undefined): boolean =>
  promotion !== undefined && promotion in PROMOTION_PLAN_PRICES;

export const getPromotionPrices = (
  promotion: string | undefined
): Record<PlanKey, number> | undefined =>
  promotion !== undefined ? PROMOTION_PLAN_PRICES[promotion] : undefined;

export const applyPromotionPrices = (
  basePlans: PlanItem[],
  promotion: string | undefined
): PlanItem[] => {
  const overrides = getPromotionPrices(promotion);
  if (!overrides) return basePlans;
  return basePlans.map((p) => ({ ...p, price: overrides[p.key] ?? p.price }));
};

// 전용 결제창 상단 띠 라벨. 해당 없으면 undefined → 띠 미노출.
export const getDedicatedPaymentBannerLabel = (
  promotion: string | undefined
): string | undefined =>
  promotion !== undefined ? PROMOTION_BANNER_LABELS[promotion] : undefined;

// 파트너 결제 단독 페이지 슬러그 → 기존 promotion 키 매핑.
// 예: /partner/titan-education-group/payment → "타이탄교육그룹" (가격/배너 재사용)
// 신규 파트너 추가 시 PROMOTION_PLAN_PRICES/PROMOTION_BANNER_LABELS 와 함께 여기 등록.
const PARTNER_SLUG_TO_PROMOTION: Record<string, string> = {
  "titan-education-group": "타이탄교육그룹",
  salmclinic: "삶클리닉",
};

export const getPromotionBySlug = (
  slug: string | undefined
): string | undefined => (slug ? PARTNER_SLUG_TO_PROMOTION[slug] : undefined);

// confirm 정합성 검증용 promotion 영문 코드. 서버가 confirm payload 의 promotion 과
// 유저 레코드의 promotion 을 교차 검증한다. 파트너 slug(PARTNER_SLUG_TO_PROMOTION)와 동일 값 체계.
// 신규 프로모션 추가 시 PROMOTION_PLAN_PRICES 와 함께 여기 등록.
const PROMOTION_TO_CODE: Record<string, string> = {
  타이탄교육그룹: "titan-education-group",
  코리안매니아: "koreanmania",
  삶클리닉: "salmclinic",
};

export const getPromotionCode = (
  promotion: string | undefined
): string | undefined =>
  promotion !== undefined ? PROMOTION_TO_CODE[promotion] : undefined;

// 플랜 카드 가격 단위에 표기하는 프로그램 기간(일). 기본 100일, 제휴 혜택(+30일)이 붙는 promotion 은 오버라이드.
export const DEFAULT_PROGRAM_DAYS = 100;
const PROMOTION_PROGRAM_DAYS: Record<string, number> = {
  삶클리닉: 130,
};
export const getPromotionProgramDays = (
  promotion: string | undefined
): number =>
  (promotion !== undefined ? PROMOTION_PROGRAM_DAYS[promotion] : undefined) ??
  DEFAULT_PROGRAM_DAYS;

// 빨강(Alert) 계열로 강조하는 플랜 태그. 태그 색 분기에서 일반(SoftHighlight)과 구분.
// "가장 밀도 있는"(기본 마스터) / "가장 빠른 효과"(paymentPageType "s" 마스터).
export const ALERT_PLAN_TAGS = ["가장 밀도 있는", "가장 빠른 효과"];
export const isAlertTag = (tag: string | undefined): boolean =>
  tag !== undefined && ALERT_PLAN_TAGS.includes(tag);

// paymentPageType === "p" / "s" : 마스터 플랜 features 최상단에 "수석 코치 배정" 행 노출.
// INFO_CONTENTS 의 "lead_coach" key 와 매핑.
export const PAYMENT_PAGE_TYPE_P_MASTER_FEATURE: PlanFeature = {
  key: "lead_coach",
  text: "수석 코치 배정",
  included: true,
  info: true,
};

export const applyPaymentPageType = (
  basePlans: PlanItem[],
  paymentPageType: string | undefined
): PlanItem[] => {
  if (paymentPageType !== "p" && paymentPageType !== "s") return basePlans;
  return basePlans.map(
    (plan: PlanItem): PlanItem =>
      plan.key === "master"
        ? {
            ...plan,
            features: [PAYMENT_PAGE_TYPE_P_MASTER_FEATURE, ...plan.features],
          }
        : plan
  );
};

// paymentPageType === "s" : 챌린저 태그 제거, 마스터 태그를 "가장 빠른 효과"(빨강/Alert)로 오버라이드.
// 기본 선택 플랜은 호출부(각 결제 페이지)에서 마스터로 지정한다.
export const PAYMENT_PAGE_TYPE_S_MASTER_TAG = "가장 빠른 효과";
export const applyPaymentPageTypeSTags = (
  basePlans: PlanItem[],
  paymentPageType: string | undefined
): PlanItem[] => {
  if (paymentPageType !== "s") return basePlans;
  return basePlans.map((plan: PlanItem): PlanItem => {
    if (plan.key === "challenger") return { ...plan, tag: undefined };
    if (plan.key === "master")
      return { ...plan, tag: PAYMENT_PAGE_TYPE_S_MASTER_TAG };
    return plan;
  });
};

// q 결제 페이지 마스터 플랜 features 최상단 라벨: 수석 코치 배정 / 코칭 핏 보장 / 플렉서블 홀딩.
export const PAYMENT_PAGE_TYPE_Q_MASTER_FEATURES: PlanFeature[] = [
  { key: "lead_coach", text: "수석 코치 배정", included: true, info: true },
  { key: "coaching_fit", text: "코칭 핏 보장", included: true, info: true },
  {
    key: "flexible_holding",
    text: "플렉서블 홀딩",
    included: true,
    info: true,
  },
];

// 결제 페이지 마스터 플랜 features 가공.
// - "수석 코치 배정"(lead_coach)은 paymentPageType 과 무관하게 항상 노출(이미 있으면 중복 추가 안 함).
// - q/r 은 그 아래에 "코칭 핏 보장 / 플렉서블 홀딩"을 함께 노출.
// - q 만 master 플랜 tag 를 "가장 인기 있는" 으로 오버라이드 (r 은 기본 tag 유지 → 챌린저가 "가장 인기 있는" 배지 보유).
export const applyQMasterFeatures = (
  basePlans: PlanItem[],
  paymentPageType: string | undefined
): PlanItem[] => {
  return basePlans.map((plan: PlanItem): PlanItem => {
    if (plan.key !== "master") return plan;
    if (paymentPageType === "q" || paymentPageType === "r") {
      return {
        ...plan,
        tag: paymentPageType === "q" ? "가장 인기 있는" : plan.tag,
        features: [...PAYMENT_PAGE_TYPE_Q_MASTER_FEATURES, ...plan.features],
      };
    }
    const hasLeadCoach = plan.features.some((f) => f.key === "lead_coach");
    return hasLeadCoach
      ? plan
      : {
          ...plan,
          features: [PAYMENT_PAGE_TYPE_P_MASTER_FEATURE, ...plan.features],
        };
  });
};
